import {
  CommerceCategory,
  CommerceProduct,
  CommerceProductRepresentedProduct,
  Maybe,
  Query,
} from '@sonos-inc/web-lib-appsync'
import { SanitySEO } from '@sonos-inc/web-lib-types'
import { FootnotesFactory, Plp, SEO } from '@sonos-inc/www-components'
import {
  getCategoryPids,
  getContentPids,
  PlpFiltersProvider,
  useFootnotes,
  UserContentProvider,
} from '@sonos-inc/www-contexts'
import { fallbackLocaleQuery, plpQuery } from '@sonos-inc/www-queries'
import {
  fallbackPlaceholderLocale,
  getPageRevalidateConfig,
  resolveLocale,
  resolveLocaleRedirect,
} from '@sonos-inc/www-routing-helpers'
import {
  determineRedirectResponse,
  fetchData,
  fetchHeaderSubcategories,
  fetchPageData,
  fetchSplashPage,
  getClient,
  resolvePreviewDate,
} from '@sonos-inc/www-server'
import { logger } from '@sonos-inc/www-server/utils/logger'
import {
  DistinctFilterAttributes,
  FilterableCommerceCategory,
  FilterableCommerceProductSearchHit,
  FilterAttributes,
} from '@sonos-inc/www-types'
import {
  GetStaticPaths,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from 'next'
import React from 'react'

function PlpPage(props: InferGetStaticPropsType<typeof getStaticProps>) {
  const { category, footnotes } = props ?? {}
  const categoryContent = category.content?.[0] ?? {}
  const categoryPids = getCategoryPids(category)
  const allPids = getContentPids(categoryContent?.content)
  categoryPids.forEach((p) => allPids.add(p))

  const { setFootnotes } = useFootnotes()

  React.useEffect(() => {
    setFootnotes(footnotes)
  }, [footnotes, setFootnotes])

  return (
    <>
      <SEO
        {...(categoryContent.seo as SanitySEO)}
        category={category}
        contentType="category"
        slug={categoryContent.slug?.current ?? undefined}
        scheduledSeo={categoryContent.scheduledSeo}
      />

      <UserContentProvider
        key={`PLP-user-content-${category.id}`}
        pids={Array.from(allPids)}
      >
        <PlpFiltersProvider category={category}>
          <Plp category={category} />
        </PlpFiltersProvider>
      </UserContentProvider>
    </>
  )
}

export default PlpPage

/*
// Carousel (multiple media)
const reduceProductHit = (hit: Maybe<CommerceProductSearchHit>) => {
  const fallbackVariantId = hit?.representedProducts?.find(
    (represented) => represented?.product?.data?.[0]
  )?.id

\  const defaultVariantId =
    hit?.managedProduct?.defaultVariantId ?? hit?.productId ?? fallbackVariantId

  if (hit?.representedProducts) {
    hit.representedProducts = hit.representedProducts.filter(
      (rp) =>
        rp?.product?.data?.[0] &&
        (rp.id === defaultVariantId || rp.id === fallbackVariantId)
    )
  }

  if (hit?.content?.[0]?.media) {
    const filteredMedia = hit.content[0].media.filter((m) =>
      m?.mediaTags?.includes(defaultVariantId!)
    )

    if (filteredMedia.length) hit.content[0].media = filteredMedia
  }

  return hit
}
*/

let distinctAttributes: DistinctFilterAttributes = {}

const isInStock = (product: CommerceProduct) => {
  const baseStockLevel = product.inventory?.stockLevel ?? 0

  if (baseStockLevel > 0 && !product.inventory?.preorderable) return true

  const variantStock =
    product.variants?.filter((variant) => {
      const variants = variant?.product?.data?.filter((prd) => {
        const variantStockLevel = prd?.inventory?.stockLevel ?? 0

        return variantStockLevel > 0 && !prd?.inventory?.preorderable
      })

      return variants && variants.length > 0
    }) ?? []

  return variantStock.length > 0
}

const addDistinctAttribute = (key: any, value: any) => {
  if (!(key in distinctAttributes)) {
    distinctAttributes[key] = new Set()
  }

  distinctAttributes[key].add(value)
}

function determineStringFiltersAndInStock(
  hit: FilterableCommerceProductSearchHit | null,
  filterAttributes: FilterAttributes
) {
  if (hit?.expandedProductData && hit.expandedProductData.filterData) {
    const filters = JSON.parse(
      hit.expandedProductData.filterData
    ) as FilterAttributes

    hit.inStock = isInStock(hit.expandedProductData)

    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'price' || key === 'color') {
        return
      }

      filterAttributes[key] = value ?? []

      if (Array.isArray(value)) {
        value.forEach((filter) => {
          addDistinctAttribute(key, filter)
        })
      }
    })
  }
}

const setIndividualFilterAttributes = (
  hit: Maybe<FilterableCommerceProductSearchHit>
) => {
  let filterAttributes: FilterAttributes = {}

  determineStringFiltersAndInStock(hit, filterAttributes)

  const colorAttr = hit?.variationAttributes?.filter((va) => {
    return va?.id === 'color'
  })

  if (colorAttr) {
    let values = [] as string[]
    colorAttr[0]?.values?.forEach((color) => {
      if (color?.value) {
        addDistinctAttribute('color', color.value)
        values.push(color.value)
      }
    })

    filterAttributes['color'] = values
  } else {
    filterAttributes['color'] = hit?.expandedProductData?.color
      ? [hit.expandedProductData.color]
      : []
  }

  hit!.filterAttributes = filterAttributes
}

function buildFilteredMedia(
  hit: FilterableCommerceProductSearchHit | null
): void {
  if (hit?.content?.[0]?.media) {
    const variants = hit.representedProducts?.[0]?.product?.data?.[0]?.variants
    let variantMediaSet: any = {}
    const filteredMedia: any = []

    if (variants?.length) {
      variants.forEach((variant) => {
        if (variant?.productId && !variantMediaSet[`${variant.productId}`]) {
          variantMediaSet[`${variant.productId}`] =
            hit.content?.[0]?.media?.find((m) =>
              m?.mediaTags?.includes(variant.productId!)
            )
        }
      })

      Object.entries(variantMediaSet).forEach(([, media]) => {
        if (media) {
          filteredMedia.push(media)
        }
      })
    } else {
      // for product with no color variants, return just the first image
      filteredMedia.push(hit.content[0]?.media[0])
    }

    hit.content[0].media = filteredMedia
  }
}

const reduceGalleryDataToOnePerVariant = (
  hit: Maybe<FilterableCommerceProductSearchHit>
) => {
  const fallbackVariantId =
    hit?.representedProducts?.find(
      (represented) =>
        represented?.product?.data?.[0] &&
        represented.product.data[0].id?.includes(hit.productId!)
    )?.id ?? hit?.productId

  const defaultVariantId =
    hit?.content?.[0]?.externalDefaultVariantId ?? fallbackVariantId

  if (hit?.representedProducts) {
    hit.representedProducts = hit.representedProducts.filter(
      (rp) =>
        rp?.product?.data?.[0] &&
        (rp.id === defaultVariantId || rp.id === fallbackVariantId)
    )
    if (!hit.representedProducts.length) {
      hit.representedProducts[0] = {
        id: hit.productId,
        product: { data: [hit.expandedProductData] },
      } as CommerceProductRepresentedProduct
    }
  }

  buildFilteredMedia(hit)

  setIndividualFilterAttributes(hit)

  return hit
}

const isHyperPLP = (category: CommerceCategory) =>
  category.showSubCatsOnPLP === 'yes'

function trimCategoryData(category: FilterableCommerceCategory) {
  // Filter unnecessary categories
  if (isHyperPLP(category)) {
    category.categories = category.categories?.filter(
      (subcategory) => subcategory?.showOnPLP !== 'no'
    )
    delete category.products
  } else if (category.products) {
    category.products.hits = category.products.hits
      ?.filter(
        (hit) =>
          hit?.content?.[0] &&
          hit.content[0].media?.length &&
          hit.content[0].name
      )
      .map(reduceGalleryDataToOnePerVariant)

    let distinctFilters: FilterAttributes = {}

    Object.entries(distinctAttributes).forEach(([key, value]) => {
      distinctFilters[key] = Array.from(value)
    })

    category.distinctFilters = distinctFilters!

    delete category.categories
  }
}

function checkNotFoundOrRedirectResponse(
  nextLocale: string | undefined,
  cid: string | string[] | undefined
) {
  let response
  if (nextLocale === fallbackPlaceholderLocale) {
    response = { notFound: true }
  }

  const localeRedirect = resolveLocaleRedirect(nextLocale, 'shop', cid)
  if (localeRedirect) {
    response = localeRedirect
  }

  const locale = resolveLocale(nextLocale, '')

  if (!locale || !cid) {
    response = { notFound: true }
  }

  return { locale, response }
}

function categoryDisabled(
  notFound: boolean,
  isOffline: boolean | null | undefined
) {
  return notFound || isOffline
}

export const getStaticProps = async (ctx: GetStaticPropsContext) => {
  const { params = {}, locale: nextLocale, previewData } = ctx
  const { cid } = params

  const { locale, response: notFoundOrRedirectResponse } =
    checkNotFoundOrRedirectResponse(nextLocale, cid)

  if (notFoundOrRedirectResponse) return notFoundOrRedirectResponse

  let props: Record<string, any> = {}
  let errorData: Record<string, any> | null = null

  let logPrefix = `PLP-/${locale}/shop/${cid}`

  try {
    const commerceData = await fetchData<Query>(plpQuery, {
      cids: cid,
      locale,
    })

    distinctAttributes = {}

    const customerGroups = (commerceData?.getPersonalizations
      .userPersonalizations?.customerGroups ?? []) as Array<string>
    const { categories } = commerceData?.getCommerce ?? {}
    const category = categories?.data?.[0] as Maybe<FilterableCommerceCategory>

    let notFound = !category

    const client = getClient(false)

    const fallbackLocale = await client.fetch<string>(fallbackLocaleQuery, {
      locale,
    })

    if (category) {
      trimCategoryData(category)
      notFound = isHyperPLP(category)
        ? !category.categories?.[0]
        : !category.products?.hits?.[0]
    }

    const slug = category?.content?.[0]?.slug?.current ?? ''
    const isOffline = category?.content?.[0]?.offline

    logPrefix = `PLP-/${locale}/${slug}`

    if (categoryDisabled(notFound, isOffline)) {
      // 99% a disabled product
      logger.info(`${logPrefix}: Category is disabled or completely missing`)

      return determineRedirectResponse(locale, slug, `${cid}`)
    }

    if (!slug) {
      errorData = commerceData as any
      logger.error(
        errorData,
        `${logPrefix}: Severe Product Data Error - content slug not available from product data`
      )
      throw new Error('Severe Category Data error - missing content slug')
    }

    const previewDate = resolvePreviewDate(previewData)

    const [
      navHeaderSubCategoryData,
      {
        currentLocale,
        forms,
        microcopy,
        dtcValueProps,
        svgs,
        header,
        footer,
        ribbon,
      },
      splashPage,
    ] = await Promise.all([
      fetchHeaderSubcategories(locale),
      fetchPageData(
        client,
        locale,
        [
          'icon-chevron.svg',
          'icon-compare.svg',
          'icon-left-chevron-arrow.svg',
          'icon-left-chevron-arrow-mobile.svg',
          'icon-right-chevron-arrow.svg',
          'icon-right-chevron-arrow-mobile.svg',
          'icon-external-link.svg',
          'icon-close.svg',
          'icon-search.svg',
          'icon-loader.svg',
          'avatar-svc.svg',
          'icon-filters.svg',
          'icon-x-filled.svg',
          'icon-arrow-up.svg',
          'icon-arrow-down.svg',
          'icon-checkbox-empty.svg',
        ],
        ['www'],
        ['search'],
        slug,
        customerGroups,
        fallbackLocale,
        previewDate
      ),
      fetchSplashPage(
        client,
        locale,
        fallbackLocale,
        process.env.SPLASH_PAGE_ID
      ),
    ])

    const categoryContent = category?.content?.[0] || {}

    const footnotesFactory = new FootnotesFactory(previewDate)
    const footnotes = footnotesFactory
      .setSearchSource([categoryContent])
      .search()
    const footnotesJumpLinkEnabled = categoryContent.footnotesJumpLinkEnabled

    props = {
      microcopy,
      forms,
      dtcValueProps,
      currentLocale,
      svgs,
      category,
      locale,
      id: cid,
      header,
      footer,
      ribbon,
      previewDate,
      navHeaderSubCategoryData,
      splashPage,
      footnotesJumpLinkEnabled,
      footnotes,
    }
  } catch (error) {
    const errorDescription =
      'Severe error fetching data while generating static props'
    logger.error(errorData, `${logPrefix}: ${errorDescription}`, error)

    return determineRedirectResponse(locale, `${cid}`)
  }

  if (process.env.VERBOSE_LOGS) {
    logger.info(
      `Returning built page for ${locale}, ${cid}. Caching for long? ${!!props.navHeaderSubCategoryData}, ${
        !!props.navHeaderSubCategoryData
          ? process.env.NEXT_PAGE_REVALIDATE_SECONDS || 1800
          : 60
      }`
    )
  }

  if (props.category) {
    return {
      props,
      revalidate: getPageRevalidateConfig(props.navHeaderSubCategoryData),
    }
  }

  const errorMessage = 'Severe error rendering PLP due to insufficient data'
  logger.error(locale, cid, errorMessage, errorData, props)

  return determineRedirectResponse(locale, `${cid}`)
}

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  if (process.env.PREBUILD_PAGES !== 'true') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  const paths = [] as { locale: string; params: { cid: string } }[]
  for (const locale of locales ?? []) {
    paths.push({
      params: {
        cid: 'shopall',
      },
      locale,
    })
  }

  return {
    paths,
    fallback: 'blocking',
  }
}
